import type {
  AddToSequenceConfig,
  SequenceAction,
  SequenceProvider,
  SequenceSenderMode,
} from '@/types/custom-playbooks';
import type { EngageSequence } from '@/store/features/engage.feature';
import type { SequencerUser } from '@/types/sequencers';
import type { RunNote } from './run-notes';
import { BRAND } from '@/lib/brand';
import { PLAY_VALUE_KEY } from '@/utils/write-skill.util';

/**
 * The play step "Add to sequence": its card words and what a run of it says.
 * Pure, so the words are tested here. The server's side is
 * `utils/play_sequence_step.py` and `temporal/activities/engage_play.py`.
 */

/** The code a person the step left out carries on their run record. */
export const SEQUENCE_SKIP_CODE = 'sequence_skipped';

/** The code a person an agent's play added with no email carries on their
 *  run record: the sequence's call or LinkedIn step reaches them. */
export const SEQUENCE_WITHOUT_EMAIL_CODE = 'sequence_without_email';

export type OutsideProvider = Exclude<SequenceProvider, 'cleanlist'>;

export const SEQUENCE_PROVIDER_NAMES: Record<SequenceProvider, string> = {
  cleanlist: BRAND.name,
  lemlist: 'lemlist',
  outreach: 'Outreach',
  salesloft: 'Salesloft',
  nooks: 'Nooks AI',
  instantly: 'Instantly',
};

export const SENDER_MODE_LABELS: Record<SequenceSenderMode, string> = {
  sequence_default: 'Sequence default',
  record_owner: 'Record owner',
  user: 'One user',
};

/** The step's provider: Cleanlist when none is saved. */
export function sequenceProviderOf(
  config: AddToSequenceConfig
): SequenceProvider {
  return config.provider ?? 'cleanlist';
}

/** The most days a pause step holds a person, as the API's PAUSE_DAYS_MAX. */
export const PAUSE_DAYS_MAX = 90;

/** What the step does: add when none is saved, or for a value it doesn't
 *  know (the API warns on that one). */
export function sequenceActionOf(
  config: Pick<AddToSequenceConfig, 'action'>
): SequenceAction {
  return config.action === 'pause' || config.action === 'remove'
    ? config.action
    : 'add';
}

/** A pause step's days, a whole number from 1 to 90; null otherwise. */
export function pauseDaysOf(config: AddToSequenceConfig): number | null {
  const days = config.pauseDays;
  return sequenceActionOf(config) === 'pause' &&
    typeof days === 'number' &&
    Number.isInteger(days) &&
    days >= 1 &&
    days <= PAUSE_DAYS_MAX
    ? days
    : null;
}

/** "Pause in Q4 outbound" or "Remove from Q4 outbound" for a Cleanlist
 *  pause or remove step; null for an add step. */
export function sequenceChangeWords(
  config: AddToSequenceConfig,
  name: string
): string | null {
  const action = sequenceActionOf(config);
  if (action === 'add' || isOutsideProvider(sequenceProviderOf(config)))
    return null;
  return action === 'pause' ? 'Pause in ' + name : 'Remove from ' + name;
}

export function isOutsideProvider(
  provider: SequenceProvider | undefined
): provider is OutsideProvider {
  return provider !== undefined && provider !== 'cleanlist';
}

/** The config after switching provider: the sequence, the sender and the
 *  values belong to the old provider, so they go, as the API's
 *  `_PROVIDER_KEYS`; an outside provider starts on its defaults. */
export function withProvider(
  config: AddToSequenceConfig,
  provider: SequenceProvider
): AddToSequenceConfig {
  if (sequenceProviderOf(config) === provider) return config;
  const rest: AddToSequenceConfig = { ...config };
  delete rest.provider;
  delete rest.sequenceId;
  delete rest.sequenceName;
  delete rest.sender;
  delete rest.onActiveElsewhere;
  delete rest.variables;
  if (provider === 'cleanlist') return rest;
  return {
    ...rest,
    provider,
    sender: { mode: 'sequence_default' },
    onActiveElsewhere: 'skip',
  };
}

/** The sender modes a provider takes: lemlist and Instantly send from the
 *  campaign's own senders, as the API's `CAMPAIGN_SENDERS`. */
export function senderModes(provider: OutsideProvider): SequenceSenderMode[] {
  return provider === 'lemlist' || provider === 'instantly'
    ? ['sequence_default']
    : ['sequence_default', 'record_owner', 'user'];
}

/** What the provider calls the thing people go into: an Instantly campaign,
 *  every other provider's sequence. */
export function sequenceNoun(provider: SequenceProvider): string {
  return provider === 'instantly' ? 'Campaign' : 'Sequence';
}

/** Whether an outside step's play values reach the provider: Instantly only,
 *  as the API's `takes_variables`. lemlist, Outreach, Salesloft and Nooks
 *  keep a saved `variables` block and ignore it. */
export function takesVariables(provider: SequenceProvider): boolean {
  return provider === 'instantly';
}

/** Instantly lead fields the step fills from the person, which a value can't
 *  take, as the API's `RESERVED_KEYS`. */
export const INSTANTLY_RESERVED_KEYS: ReadonlySet<string> = new Set([
  'email',
  'first_name',
  'last_name',
  'company_name',
  'job_title',
  'website',
  'phone',
  'linkedin_url',
]);

/** A campaign's declared custom variables a value can be saved under: a
 *  usable name (`PLAY_VALUE_KEY`) that isn't a lead field. */
export function campaignValueKeys(
  declared: readonly string[] | null | undefined
): string[] {
  return (declared ?? []).filter(
    (key) => PLAY_VALUE_KEY.test(key) && !INSTANTLY_RESERVED_KEYS.has(key)
  );
}

/** The config after picking a sequence. On Instantly a new campaign keeps
 *  only the values it declares (`declared`), since a run never sends a key
 *  the campaign lacks; every other provider keeps its values as before. */
export function withSequence(
  config: AddToSequenceConfig,
  picked: { id: string; name: string; variables?: readonly string[] | null }
): AddToSequenceConfig {
  const next: AddToSequenceConfig = {
    ...config,
    sequenceId: picked.id,
    sequenceName: picked.name,
  };
  if (
    !takesVariables(sequenceProviderOf(config)) ||
    picked.id === config.sequenceId ||
    !config.variables
  )
    return next;
  const keep = new Set(campaignValueKeys(picked.variables));
  const kept = Object.entries(config.variables).filter(([key]) =>
    keep.has(key)
  );
  if (kept.length) next.variables = Object.fromEntries(kept);
  else delete next.variables;
  return next;
}

/** Why an outside step's sender can't send, as cleanse-api's
 *  `sender_issue`; null when it can. */
export function sequenceSenderIssue(
  config: AddToSequenceConfig
): string | null {
  const provider = sequenceProviderOf(config);
  if (!isOutsideProvider(provider)) return null;
  const sender = config.sender ?? { mode: 'sequence_default' as const };
  if (!senderModes(provider).includes(sender.mode)) return 'Pick who sends';
  if (sender.mode === 'user' && !sender.userId) return 'Pick who sends';
  if (
    provider === 'outreach' &&
    sender.mode === 'sequence_default' &&
    !sender.fallbackUserId
  )
    return 'Pick who sends';
  return null;
}

/** What a step with nothing picked asks for: "Pick a sequence", or
 *  "Pick a campaign" on Instantly. Matches the API's `pick_words`. */
export function sequencePickWords(config: AddToSequenceConfig): string {
  return 'Pick a ' + sequenceNoun(sequenceProviderOf(config)).toLowerCase();
}

/** The card's headline: "Add to Q4 outbound", "Pause in Q4 outbound for
 *  14 days", "Remove from Q4 outbound", "Outreach · EMEA VP Sales", or
 *  "Pick a sequence". */
export function sequenceStepSummary(config: AddToSequenceConfig): string {
  if (!config.sequenceId) return sequencePickWords(config);
  const name = config.sequenceName?.trim();
  const provider = sequenceProviderOf(config);
  if (isOutsideProvider(provider)) {
    const label = SEQUENCE_PROVIDER_NAMES[provider];
    return name ? label + ' · ' + name : label;
  }
  const change = sequenceChangeWords(config, name || 'sequence');
  if (change) {
    const days = pauseDaysOf(config);
    return days === null
      ? change
      : change + ' for ' + days + (days === 1 ? ' day' : ' days');
  }
  return name ? 'Add to ' + name : 'Add to sequence';
}

/** The card's second line for an outside step: who sends. None on Instantly,
 *  whose campaign's own inboxes send. lemlist keeps its line as before. */
export function outsideSenderWords(
  config: AddToSequenceConfig,
  users?: readonly SequencerUser[]
): string | null {
  const provider = sequenceProviderOf(config);
  if (!isOutsideProvider(provider) || !config.sequenceId) return null;
  if (provider === 'instantly') return null;
  const sender = config.sender ?? { mode: 'sequence_default' as const };
  if (sender.mode === 'user') {
    const user = users?.find((u) => u.id === sender.userId);
    return 'From ' + (user?.name ?? 'one user');
  }
  return sender.mode === 'record_owner'
    ? 'From record owner'
    : 'From sequence default';
}

/** The sequences the step can pick: every one but the archived. */
export function pickableSequences(
  sequences: readonly EngageSequence[]
): EngageSequence[] {
  return sequences.filter((sequence) => !sequence.archived);
}

/** The picked sequence's status as the card shows it, with its name as it
 *  is now when it was renamed after the pick; null when the list hasn't
 *  loaded or no sequence is picked. */
export function sequenceStatusWords(
  config: AddToSequenceConfig,
  sequences: readonly EngageSequence[] | undefined
): string | null {
  if (!config.sequenceId || !sequences) return null;
  const found = sequences.find((sequence) => sequence.id === config.sequenceId);
  if (!found) return 'Not found';
  const picked = config.sequenceName?.trim();
  return picked && found.name && found.name !== picked
    ? found.name + ' · ' + found.status
    : found.status;
}

/** An Instantly campaign's state that still takes people but doesn't send,
 *  as the API's `NOT_SENDING_STATUSES`. */
const NOT_SENDING_WORDS: ReadonlyMap<string, string> = new Map([
  ['draft', 'Draft'],
  ['paused', 'Paused'],
  ['accounts_unhealthy', 'Accounts unhealthy'],
  ['bounce_protect', 'Bounce protect'],
]);

/** An Instantly campaign's status in words, as the API's
 *  `CAMPAIGN_STATUSES` names it; other providers' statuses as they come. */
export function campaignStatusWords(
  provider: SequenceProvider,
  status: string
): string {
  if (provider !== 'instantly') return status;
  return (
    NOT_SENDING_WORDS.get(status) ??
    INSTANTLY_STATUS_WORDS.get(status) ??
    status
  );
}

const INSTANTLY_STATUS_WORDS: ReadonlyMap<string, string> = new Map([
  ['active', 'Active'],
  ['completed', 'Completed'],
  ['suspended', 'Suspended'],
]);

/** "Campaign isn't sending (Paused)" from a step detail's `notSending`, as
 *  the API's `not_sending_words`; null for a campaign that sends. */
export function notSendingWords(
  detail: Record<string, unknown>
): string | null {
  const raw = detail.notSending;
  const status =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>).status
      : undefined;
  const word =
    typeof status === 'string' ? NOT_SENDING_WORDS.get(status) : undefined;
  return word ? "Campaign isn't sending (" + word + ')' : null;
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** A run detail's action: add for a detail from before pause and remove. */
function detailAction(detail: Record<string, unknown>): SequenceAction {
  return sequenceActionOf({ action: detail.action as SequenceAction });
}

/** Each action's words: the verb a test run would do, the past tense a run
 *  did, and how the sequence follows. */
const ACTION_WORDS: Record<
  SequenceAction,
  { verb: string; done: string; to: string; notDone: string }
> = {
  add: { verb: 'add', done: 'added', to: ' to ', notDone: 'Not added' },
  pause: { verb: 'pause', done: 'paused', to: ' in ', notDone: 'Not paused' },
  remove: {
    verb: 'remove',
    done: 'removed',
    to: ' from ',
    notDone: 'Not removed',
  },
};

/** " to Q4 outbound (Paused)" (" in" for pause, " from" for remove), or
 *  what of it the detail has. */
function target(detail: Record<string, unknown>): string {
  const name = text(detail.sequenceName);
  const status = text(detail.sequenceStatus);
  const to = ACTION_WORDS[detailAction(detail)].to;
  return (name ? to + name : '') + (status ? ' (' + status + ')' : '');
}

/** A test run's detail says what the step would do, never what it did: the
 *  count it would change, wouldAdd for add,
 *  wouldChange for pause and remove; null for a detail that isn't a test
 *  run's. */
function testCount(detail: Record<string, unknown>): number | null {
  const count =
    detailAction(detail) === 'add' ? detail.wouldAdd : detail.wouldChange;
  return typeof count === 'number' ? num(count) : null;
}

/** The Run test tab's line for the step: "Would add 3 to Q4 outbound
 *  (Active)", "Would pause 3 in Q4 outbound", or null for a detail that
 *  isn't a test run's. */
export function sequenceWouldDo(
  detail: Record<string, unknown>
): string | null {
  const count = testCount(detail);
  if (count === null) return null;
  const { verb } = ACTION_WORDS[detailAction(detail)];
  return 'Would ' + verb + ' ' + count.toLocaleString() + target(detail);
}

/**
 * The step's run stats in words: "40 added to Q4 outbound (Paused)", "3
 * without email" for those added for the call or LinkedIn steps, "3
 * skipped, missing email" per reason and "2 more skipped" for any no reason
 * names, then "Couldn't add 700 to Q4 outbound" when the step stopped part
 * way. A test run says "Would add 3" and "Would skip 1, already enrolled".
 * An Instantly campaign that takes people but doesn't send says "Campaign
 * isn't sending (Paused)" after the first line.
 */
export function sequenceRunNotes(
  detail: Record<string, unknown>,
  isDryRun = false
): RunNote[] {
  const action = detailAction(detail);
  const words = ACTION_WORDS[action];
  const tested = testCount(detail);
  const dry = isDryRun || tested !== null;
  const done = num(action === 'add' ? detail.added : detail.changed);
  const count = tested ?? done;
  const skipped = num(
    dry ? (detail.wouldSkip ?? detail.skipped) : detail.skipped
  );
  const notes: RunNote[] = [
    {
      text: dry
        ? 'Would ' + words.verb + ' ' + count.toLocaleString() + target(detail)
        : count.toLocaleString() + ' ' + words.done + target(detail),
      tone: 'muted',
    },
  ];
  const notSending = notSendingWords(detail);
  if (notSending) notes.push({ text: notSending, tone: 'muted' });
  const withoutEmail = action === 'add' ? num(detail.withoutEmail) : 0;
  if (withoutEmail > 0)
    notes.push({
      text:
        (dry ? 'Would add ' : '') +
        withoutEmail.toLocaleString() +
        ' without email',
      tone: 'muted',
    });
  let named = 0;
  const reasons = detail.skipReasons;
  if (reasons && typeof reasons === 'object' && !Array.isArray(reasons)) {
    Object.entries(reasons)
      .map(([reason, n]) => [reason, num(n)] as const)
      .filter(([, n]) => n > 0)
      .sort((a, b) => b[1] - a[1])
      .forEach(([reason, n]) => {
        named += n;
        const why = reason.charAt(0).toLowerCase() + reason.slice(1);
        notes.push({
          text: dry
            ? 'Would skip ' + n.toLocaleString() + ', ' + why
            : n.toLocaleString() + ' skipped, ' + why,
          tone: 'muted',
        });
      });
  }
  const unnamed = skipped - named;
  if (unnamed > 0)
    notes.push({
      text: dry
        ? 'Would skip ' + unnamed.toLocaleString() + (named ? ' more' : '')
        : unnamed.toLocaleString() + (named ? ' more' : '') + ' skipped',
      tone: 'muted',
    });
  const failed = num(detail.failed);
  if (failed > 0) {
    const name = text(detail.sequenceName);
    notes.push({
      text:
        "Couldn't " +
        words.verb +
        ' ' +
        failed.toLocaleString() +
        (name ? words.to + name : ''),
      tone: 'fail',
    });
  }
  return notes;
}

/** A person's reason on their run record: "Not added: Missing email",
 *  "Not paused: Not in sequence", "Not removed: Already ended", or "Added
 *  without email". */
export function sequenceRecordReason(
  error: Record<string, unknown>
): RunNote | null {
  if (error.code === SEQUENCE_WITHOUT_EMAIL_CODE)
    return { text: 'Added without email', tone: 'muted' };
  if (error.code !== SEQUENCE_SKIP_CODE) return null;
  const reason = typeof error.reason === 'string' ? error.reason : '';
  const { notDone } = ACTION_WORDS[detailAction(error)];
  return { text: reason ? notDone + ': ' + reason : notDone, tone: 'muted' };
}
